import { type SearchBlockedUsersQueryVariables } from '@exam-center/graphql';
import { dataGridSortDirection } from '@shared/components';
import { parseAsPageNumber } from '@shared/utils';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useMemo } from 'react';

import {
  ALL_BLOCKED_TYPES,
  ORDER_TYPE,
  type PickRawBlockedRecordBlockedType,
} from '../../constants';
import { type DataGridSortDirection } from '../../types';

export default function useAdminBlockedUsersListSearchParams() {
  const [{ page, pageSize, sortOrder, sortBy, search, blockedTypes, examCenters }, setQueryParams] =
    useQueryStates(
      {
        page: parseAsPageNumber.withDefault(0),
        pageSize: parseAsInteger.withDefault(10),
        sortBy: parseAsString.withDefault('firstName'),
        sortOrder: parseAsStringLiteral(dataGridSortDirection).withDefault('asc'),
        search: parseAsString.withDefault(''),
        blockedTypes: parseAsArrayOf(parseAsStringLiteral(ALL_BLOCKED_TYPES)).withDefault([]),
        examCenters: parseAsArrayOf(parseAsString).withDefault([]),
      },
      {
        urlKeys: {
          search: 'q',
        },
      },
    );

  const changePageNumber = (page: number) => {
    setQueryParams({ page });
  };

  const changePageSize = (pageSize: number) => {
    setQueryParams({ pageSize, page: 0 });
  };

  const changeSortingField = (sortBy: string, sortOrder: DataGridSortDirection) => {
    setQueryParams({ sortBy, sortOrder });
  };

  const changeSearch = (value: string) => {
    setQueryParams({ search: value, page: 0 });
  };

  const changeBlockedTypes = (value: PickRawBlockedRecordBlockedType[]) => {
    setQueryParams({ blockedTypes: value, page: 0 });
  };

  const changeExamCenters = (value: string[]) => {
    setQueryParams({ examCenters: value, page: 0 });
  };

  const searchBlockedUsersInput = useMemo(() => {
    return {
      searchBlockedUsersInput: {
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search,
          },
        },
        blockedTypes: blockedTypes,
        examCenterCodes: examCenters,
        order: {
          field: sortBy,
          type: ORDER_TYPE[sortOrder] || 'ASC', // Handle fallback
        },
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
      },
    } satisfies SearchBlockedUsersQueryVariables;
  }, [page, pageSize, sortOrder, sortBy, search, blockedTypes, examCenters]);

  return [
    {
      page,
      pageSize,
      sortOrder,
      sortBy,
      search,
      blockedTypes,
      examCenters,
      searchBlockedUsersInput,
    },
    {
      changePageNumber,
      changePageSize,
      changeSortingField,
      changeSearch,
      changeBlockedTypes,
      changeExamCenters,
    },
  ] as const;
}
